/**
 * Pure text-manipulation helpers for a Djot markup editor.
 *
 * Every function takes an `EditorSelection` (the full text plus a selection
 * range) and returns a new `EditorSelection` — never touches the DOM. This
 * keeps the formatting logic easy to reason about and reuse from a toolbar,
 * keyboard shortcuts, or (if ever needed) tests.
 *
 * Note on Djot vs. Markdown: Djot swaps the meaning of `*` and `_` relative
 * to CommonMark — `*text*` is STRONG (bold) and `_text_` is emphasis
 * (italic). See https://djot.net — "Emphasis/strong".
 */

export interface EditorSelection {
  value: string;
  start: number;
  end: number;
}

function getLineBounds(text: string, index: number): [number, number] {
  const start = text.lastIndexOf('\n', index - 1) + 1;
  let end = text.indexOf('\n', index);
  if (end === -1) end = text.length;
  return [start, end];
}

function getSelectedLineBounds(text: string, start: number, end: number): [number, number] {
  const [lineStart] = getLineBounds(text, start);
  // If the selection's end lands exactly on a line boundary (e.g. the user
  // selected through a trailing newline), don't drag in the next, unrelated
  // line.
  const adjustedEnd = end > start && text[end - 1] === '\n' ? end - 1 : end;
  const [, lineEnd] = getLineBounds(text, adjustedEnd);
  return [lineStart, lineEnd];
}

/**
 * Wraps (or, if already wrapped, unwraps) the selection with inline
 * delimiters. Falls back to `placeholder` text when nothing is selected, so
 * the user has something to type over.
 */
export function toggleInlineWrap(
  sel: EditorSelection,
  before: string,
  after: string,
  placeholder: string,
): EditorSelection {
  const { value, start, end } = sel;
  const selected = value.slice(start, end);

  // Selection already includes the delimiters — unwrap.
  if (
    selected.length >= before.length + after.length &&
    selected.startsWith(before) &&
    selected.endsWith(after)
  ) {
    const inner = selected.slice(before.length, selected.length - after.length);
    const newValue = value.slice(0, start) + inner + value.slice(end);
    return { value: newValue, start, end: start + inner.length };
  }

  // Cursor sits inside already-wrapped text with nothing selected — unwrap
  // around the cursor.
  const beforeSlice = value.slice(Math.max(0, start - before.length), start);
  const afterSlice = value.slice(end, end + after.length);
  if (!selected && beforeSlice === before && afterSlice === after) {
    const newValue =
      value.slice(0, start - before.length) + value.slice(start, end) + value.slice(end + after.length);
    const pos = start - before.length;
    return { value: newValue, start: pos, end: pos };
  }

  const content = selected || placeholder;
  const newValue = value.slice(0, start) + before + content + after + value.slice(end);
  return { value: newValue, start: start + before.length, end: start + before.length + content.length };
}

/** Toggles a literal per-line prefix (e.g. `"> "` for blockquotes) across every line touched by the selection. */
export function toggleLinePrefix(sel: EditorSelection, prefix: string): EditorSelection {
  const { value, start, end } = sel;
  const [lineStart, lineEnd] = getSelectedLineBounds(value, start, end);
  const block = value.slice(lineStart, lineEnd);
  const lines = block.split('\n');
  const nonBlank = lines.filter((l) => l.trim() !== '');
  const allPrefixed = nonBlank.length > 0 && nonBlank.every((l) => l.startsWith(prefix));

  const newLines = lines.map((l) => {
    if (l.trim() === '') return l;
    return allPrefixed ? l.slice(prefix.length) : prefix + l;
  });

  const newBlock = newLines.join('\n');
  const newValue = value.slice(0, lineStart) + newBlock + value.slice(lineEnd);
  const delta = newBlock.length - block.length;
  return { value: newValue, start: lineStart, end: Math.max(lineStart, lineEnd + delta) };
}

/** Toggles a numbered ordered-list prefix (`"1. "`, `"2. "`, …) across every line touched by the selection. */
export function toggleOrderedList(sel: EditorSelection): EditorSelection {
  const { value, start, end } = sel;
  const [lineStart, lineEnd] = getSelectedLineBounds(value, start, end);
  const block = value.slice(lineStart, lineEnd);
  const lines = block.split('\n');
  const orderedRe = /^\d+\.\s/;
  const nonBlank = lines.filter((l) => l.trim() !== '');
  const allOrdered = nonBlank.length > 0 && nonBlank.every((l) => orderedRe.test(l));

  let n = 1;
  const newLines = lines.map((l) => {
    if (l.trim() === '') return l;
    if (allOrdered) return l.replace(orderedRe, '');
    const out = `${n}. ${l}`;
    n += 1;
    return out;
  });

  const newBlock = newLines.join('\n');
  const newValue = value.slice(0, lineStart) + newBlock + value.slice(lineEnd);
  const delta = newBlock.length - block.length;
  return { value: newValue, start: lineStart, end: Math.max(lineStart, lineEnd + delta) };
}

/** Sets (or clears, if already at that level) an ATX heading marker on the line containing the cursor. */
export function setHeadingLevel(sel: EditorSelection, level: number): EditorSelection {
  const { value, start, end } = sel;
  const [lineStart, lineEnd] = getLineBounds(value, start);
  const line = value.slice(lineStart, lineEnd);
  const match = line.match(/^(#{1,6})\s*/);
  const currentLevel = match ? match[1].length : 0;
  const stripped = match ? line.slice(match[0].length) : line;
  const newLine = currentLevel === level ? stripped : `${'#'.repeat(level)} ${stripped}`;
  const newValue = value.slice(0, lineStart) + newLine + value.slice(lineEnd);
  const delta = newLine.length - line.length;
  return {
    value: newValue,
    start: Math.max(lineStart, start + delta),
    end: Math.max(lineStart, end + delta),
  };
}

/** Inserts a `[text](url)` link (or `![alt](url)` image), selecting the `url` placeholder for immediate editing. */
export function insertLink(sel: EditorSelection, isImage: boolean): EditorSelection {
  const { value, start, end } = sel;
  const selected = value.slice(start, end) || (isImage ? 'alt text' : 'link text');
  const prefix = isImage ? '!' : '';
  const inserted = `${prefix}[${selected}](url)`;
  const newValue = value.slice(0, start) + inserted + value.slice(end);
  const urlStart = start + prefix.length + selected.length + 3; // past "[selected]("
  return { value: newValue, start: urlStart, end: urlStart + 3 };
}

/**
 * Inserts a wikilink using the familiar `[[Article Name]]` shorthand (see
 * `@/lib/wikiLinkSyntax`), selecting the article-name placeholder. This
 * shorthand is expanded to spec-compliant NIP-54 Djot (`[Article Name][]`)
 * before the document is parsed or published.
 */
export function insertWikilink(sel: EditorSelection): EditorSelection {
  const { value, start, end } = sel;
  const selected = value.slice(start, end) || 'Article name';
  const inserted = `[[${selected}]]`;
  return { value: value.slice(0, start) + inserted + value.slice(end), start: start + 2, end: start + 2 + selected.length };
}

/**
 * Inserts a self-contained block (code fence, table, thematic break, …) at
 * the cursor, adding blank-line separation from surrounding text since Djot
 * paragraphs can't be interrupted by other block-level elements.
 */
export function insertBlockAtCursor(
  sel: EditorSelection,
  buildTemplate: (selectedText: string) => string,
): EditorSelection {
  const { value, start, end } = sel;
  const selected = value.slice(start, end);
  const template = buildTemplate(selected);

  const before = value.slice(0, start);
  const leadingNewlines = before.length === 0 ? '' : /\n\n$/.test(before) ? '' : /\n$/.test(before) ? '\n' : '\n\n';

  const after = value.slice(end);
  const trailingNewlines = after.length === 0 ? '' : /^\n\n/.test(after) ? '' : /^\n/.test(after) ? '\n' : '\n\n';

  const inserted = leadingNewlines + template + trailingNewlines;
  const newValue = before + inserted + after;
  const blockStart = start + leadingNewlines.length;
  return { value: newValue, start: blockStart, end: blockStart + template.length };
}

export function codeBlockTemplate(selected: string): string {
  return `\`\`\`\n${selected || 'code here'}\n\`\`\``;
}

export function tableTemplate(): string {
  return ['| Column 1 | Column 2 |', '|----------|----------|', '| Cell     | Cell     |'].join('\n');
}

export function thematicBreakTemplate(): string {
  return '* * * *';
}

/**
 * Inserts a footnote reference (`[^n]`) at the cursor and appends a
 * placeholder definition (`[^n]: Footnote text.`) at the end of the
 * document. The cursor stays right after the reference so the user can
 * continue writing the surrounding sentence.
 */
export function insertFootnote(sel: EditorSelection): EditorSelection {
  const { value, start, end } = sel;
  const existing = [...value.matchAll(/\[\^(\d+)\]/g)].map((m) => Number(m[1]));
  const next = existing.length > 0 ? Math.max(...existing) + 1 : 1;
  const marker = `[^${next}]`;

  const beforeMarker = value.slice(0, start);
  const afterMarker = value.slice(end);
  const withMarker = beforeMarker + marker + afterMarker;
  const trimmed = withMarker.replace(/\n+$/, '');
  const suffix = `\n\n[^${next}]: Footnote text.`;
  const finalValue = trimmed + suffix;

  const cursorPos = beforeMarker.length + marker.length;
  return { value: finalValue, start: cursorPos, end: cursorPos };
}
